import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Clock } from 'lucide-react';
import { nursingNotesService, NursingNote } from '../../../lib/services.ipd';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

interface NursingNotesTabProps {
  admissionId: string;
}

export default function NursingNotesTab({ admissionId }: NursingNotesTabProps) {
  const [notes, setNotes] = useState<NursingNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingNote, setEditingNote] = useState<NursingNote | null>(null);
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm<{
    shift: 'morning' | 'evening' | 'night';
    note: string;
  }>();

  useEffect(() => {
    loadNotes();
  }, [admissionId]);

  const loadNotes = async () => {
    try {
      const data = await nursingNotesService.getByAdmission(admissionId);
      setNotes(data);
    } catch (error) {
      console.error('Failed to load nursing notes:', error);
      toast.error('Failed to load nursing notes');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: { shift: 'morning' | 'evening' | 'night'; note: string }) => {
    try {
      if (editingNote) {
        await nursingNotesService.update(editingNote.id, data);
        toast.success('Note updated successfully');
      } else {
        await nursingNotesService.create({
          admission_id: admissionId,
          note_date: new Date().toISOString(),
          shift: data.shift,
          note: data.note,
          nurse_id: '',
        });
        toast.success('Note added successfully');
      }
      reset();
      setShowAddForm(false);
      setEditingNote(null);
      loadNotes();
    } catch (error) {
      console.error('Failed to save note:', error);
      toast.error('Failed to save note');
    }
  };

  const handleEdit = (note: NursingNote) => {
    setEditingNote(note);
    setShowAddForm(true);
    reset({
      shift: note.shift,
      note: note.note,
    });
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Nursing Notes</h3>
        <button
          onClick={() => {
            setShowAddForm(!showAddForm);
            setEditingNote(null);
            reset();
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Add Note
        </button>
      </div>

      {showAddForm && (
        <form onSubmit={handleSubmit(onSubmit)} className="bg-gray-50 p-4 rounded-lg space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Shift <span className="text-red-500">*</span>
            </label>
            <select
              {...register('shift', { required: 'Shift is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select shift</option>
              <option value="morning">Morning</option>
              <option value="evening">Evening</option>
              <option value="night">Night</option>
            </select>
            {errors.shift && <p className="text-sm text-red-600 mt-1">{errors.shift.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note <span className="text-red-500">*</span>
            </label>
            <textarea
              {...register('note', { required: 'Note is required' })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Enter nursing observations and care provided..."
            />
            {errors.note && <p className="text-sm text-red-600 mt-1">{errors.note.message}</p>}
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {editingNote ? 'Update Note' : 'Save Note'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                setEditingNote(null);
                reset();
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {notes.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No nursing notes recorded yet</p>
        ) : (
          notes.map((note) => (
            <div key={note.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {new Date(note.note_date).toLocaleString()}
                  </span>
                  <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full capitalize">
                    {note.shift}
                  </span>
                </div>
                <button
                  onClick={() => handleEdit(note)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>
              <p className="text-gray-800 whitespace-pre-wrap">{note.note}</p>
              <p className="text-xs text-gray-500 mt-2">By: {note.nurse?.email}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
